import { GeneratedScenePack } from './NexoraXGeneratedGuideVideo';

export const NEXORAX_SCENE_TEMPLATES: Record<string, GeneratedScenePack> = {
  "welcome-to-nexorax": {
    guideId: "welcome-to-nexorax",
    videoTitle: "Welcome to NexoraX",
    introLine: "Learn how the NexoraX ecosystem unifies your operations seamlessly.",
    durationTarget: "1:45",
    ctaEnding: "Explore Ecosystem",
    scenes: [
      {
        title: "Unified Control",
        text: "Your entire enterprise in one dashboard.",
        voiceover: "Welcome to NexoraX. Place all your dynamic workspaces, active portals, and communications channels under one unified, secure corporate umbrella.",
        visualDirection: "A rotating 3D metallic grid glowing with cyan/purple particle flows, centering the NexoraX communications crest."
      },
      {
        title: "Interactive Dashboard",
        text: "High-performance functional channels.",
        voiceover: "Discover the central administration console where you can monitor real-time indicators and customize critical actions with zero overhead.",
        visualDirection: "Dynamic futuristic system analytics graph with animating lines and flashing terminal reports."
      },
      {
        title: "Business Integrations",
        text: "Secure customer contact and workflow bridges.",
        voiceover: "Bridge your public presence with internal databases seamlessly. Set up workflows that convert inbound support enquiries to instantly actionable tasks.",
        visualDirection: "Flowchart style connections lighting up sequentially as data moves from a web icon to a database vault."
      },
      {
        title: "High-Security Environment",
        text: "Enterprise-level protection.",
        voiceover: "Your operations are secured by the rigorous NexoraX protocol, keeping wedding lists or corporate records safe with absolute and uncompromising assurance.",
        visualDirection: "A sleek, glowing digital gold padlocked seal pulsating with deep violet security orbits."
      },
      {
        title: "Continuous Operations",
        text: "24/7 autonomous support modules.",
        voiceover: "Let autonomous agents carry out background processing and automated updates so your systems remain operational and healthy round the clock.",
        visualDirection: "An elegant, spinning blue sphere surrounded by orbiting rings with bright data hubs."
      }
    ]
  },
  "nexus-search-guide": {
    guideId: "nexus-search-guide",
    videoTitle: "How to Use NexoraX Search",
    introLine: "Find and analyze business targets with unparalleled velocity.",
    durationTarget: "3:20",
    ctaEnding: "Open NexoraX Search",
    scenes: [
      {
        title: "Search Smarter",
        text: "Find business opportunities faster.",
        voiceover: "NexoraX Search helps you search across business topics, products, suppliers, competitors, and research areas.",
        visualDirection: "High-contrast search hub visualization with illuminating nodes spreading across a global grid."
      },
      {
        title: "Choose a Category",
        text: "Web, Business, Products, Suppliers, Competitors.",
        voiceover: "Start by choosing the search type that matches your goal.",
        visualDirection: "A clean five-column grid showcasing category tabs lighting up one-by-one with golden accent shadows."
      },
      {
        title: "Review the AI Summary",
        text: "Understand results quickly.",
        voiceover: "The system helps summarise useful findings so you can act faster.",
        visualDirection: "A modern glass card with streaming text and auto-generated analytical highlights fading in."
      },
      {
        title: "Save Useful Results",
        text: "Send results into your workspace.",
        voiceover: "Save findings into WorkSuite, reports, or lead research.",
        visualDirection: "A dynamic filing system graphic showing results flowing into folders marked with cyan arrows."
      },
      {
        title: "Send to Agent",
        text: "Turn search into action.",
        voiceover: "Send results to a NexoraX Agent to create a lead, report, task, or content brief.",
        visualDirection: "An abstract neural AI core node flashing and projecting formatted documents onto a dark user interface."
      }
    ]
  },
  "nexus-hosting-guide": {
    guideId: "nexus-hosting-guide",
    videoTitle: "How to Set Up NexoraX Hosting",
    introLine: "Launch fast, securely, and with comprehensive SSL certificates automatically updated.",
    durationTarget: "3:05",
    ctaEnding: "Open NexoraX Hosting",
    scenes: [
      {
        title: "Hosting Dashboard",
        text: "Check your website hosting status.",
        voiceover: "NexoraX Hosting helps you manage deployment, SSL, domain connection, and launch readiness.",
        visualDirection: "Sleek rack server animation with glowing neon LED indicators showing active healthy states."
      },
      {
        title: "Connect Domain",
        text: "Bring your domain into the workspace.",
        voiceover: "Review domain connection and DNS status before going live.",
        visualDirection: "A secure digital wire connecting a domain.com badge to a central hosting module."
      },
      {
        title: "Check SSL",
        text: "Secure your website connection.",
        voiceover: "SSL status helps confirm your site is protected for visitors.",
        visualDirection: "Green glowing shield with a digital secure lock expanding outwards."
      },
      {
        title: "Review Deployment",
        text: "Confirm build and preview status.",
        voiceover: "Use the launch checklist to check your public preview before release.",
        visualDirection: "A diagnostic overlay checklist with items automatically checking off with sleek cyan ticks."
      },
      {
        title: "Request Support",
        text: "Get help when setup needs review.",
        voiceover: "If anything needs attention, request hosting support from the dashboard.",
        visualDirection: "A pulsing support terminal interface showing direct connectivity to the Lead Director support line."
      }
    ]
  },
  "mail-domains-guide": {
    guideId: "mail-domains-guide",
    videoTitle: "How to Set Up Business Email & Domains",
    introLine: "Establish professional email addresses matching active domains.",
    durationTarget: "2:40",
    ctaEnding: "Set Up Mail & Domains",
    scenes: [
      {
        title: "Connect Your Domain",
        text: "Start with your business domain.",
        voiceover: "Mail & Domains helps connect your domain, email, DNS, and sending setup.",
        visualDirection: "Branded corporate DNS configuration view showing stylized inputs and deep space backdrops."
      },
      {
        title: "Create Business Emails",
        text: "info, support, sales, bookings.",
        voiceover: "Request professional email addresses for your business communication.",
        visualDirection: "Interactive mailboxes list sliding onto a screen showing executive branded address badges."
      },
      {
        title: "Review DNS",
        text: "A, MX, TXT, SPF, DKIM, DMARC.",
        voiceover: "DNS records help connect your website, email, and secure sending.",
        visualDirection: "Sleek matrix of DNS records highlighting MX and TXT records with secure gold borders."
      },
      {
        title: "Verify Sending",
        text: "Improve deliverability.",
        voiceover: "SPF, DKIM, and DMARC checks help reduce email delivery problems.",
        visualDirection: "An analytical dial showing sending health rising from warning to 100% verified status."
      },
      {
        title: "Activate Workspace",
        text: "Email, domain, and hosting connected.",
        voiceover: "Once setup is complete, everything connects into your NexoraX workspace.",
        visualDirection: "A series of gears interlocking and lighting up as they merge into the main workspace dashboard."
      }
    ]
  },
  "nexus-chat-guide": {
    guideId: "nexus-chat-guide",
    videoTitle: "How to Use NexoraX Chat",
    introLine: "Unleash responsive chat widgets that double as autonomous acquisition streams.",
    durationTarget: "1:50",
    ctaEnding: "Open NexoraX Chat",
    scenes: [
      {
        title: "Website Assistant",
        text: "Answer customer questions.",
        voiceover: "NexoraX Chat helps visitors understand your services and ask questions.",
        visualDirection: "An elegant dark chat bubble on a glass panel animating responses with simulated keystrokes."
      },
      {
        title: "Support Mode",
        text: "Guide visitors to the right help.",
        voiceover: "Use chat to answer common support and pricing questions.",
        visualDirection: "FAQ dropdown options lighting up on a website layout mockup."
      },
      {
        title: "Capture Enquiries",
        text: "Turn questions into leads.",
        voiceover: "With consent, enquiries can be saved into your workflow.",
        visualDirection: "Visitor query text turning into a stylized lead profile card with golden aura."
      },
      {
        title: "Send to WorkSuite",
        text: "Create tasks and follow-ups.",
        voiceover: "Useful conversations can become tasks, notes, or approval items.",
        visualDirection: "A glowing line connecting the active chat window to a clean task kanban list."
      },
      {
        title: "Human Approval",
        text: "External follow-ups need approval.",
        voiceover: "AI can draft replies, but outbound actions should remain approved by a human.",
        visualDirection: "A dual-key style interface where a human validator gives the final green approval stamp to an automation flow."
      }
    ]
  },
  "social-automation-guide": {
    guideId: "social-automation-guide",
    videoTitle: "How to Create Social Media Automation",
    introLine: "Distribute cohesive marketing across all social sectors effortlessly.",
    durationTarget: "3:15",
    ctaEnding: "Launch Social Automation",
    scenes: [
      {
        title: "Choose Campaign",
        text: "Start with a content goal.",
        voiceover: "Social Automation helps create captions, campaign packs, and post ideas.",
        visualDirection: "A visual post planning grid with multiple empty frames lighting up with campaign parameters."
      },
      {
        title: "Generate Captions",
        text: "Create content faster.",
        voiceover: "Generate platform-specific captions for your brand and services.",
        visualDirection: "A text field typing out multiple variants of highly engaging social posts with hashtags."
      },
      {
        title: "Build Approval Queue",
        text: "Review before publishing.",
        voiceover: "Keep content safe by reviewing posts before they go live.",
        visualDirection: "An approval slider moving from Review to Approved with an elegant particle burst."
      },
      {
        title: "Create Campaign Pack",
        text: "Posts, captions, hooks, hashtags.",
        voiceover: "Turn one idea into multiple social assets.",
        visualDirection: "A single central asset block splitting out into individual tailored banners for different platforms."
      },
      {
        title: "Export or Schedule",
        text: "Publish when ready.",
        voiceover: "Approved content can be exported or scheduled when your accounts are connected.",
        visualDirection: "A circular clock graphic turning slowly before discharging assets to the networks."
      }
    ]
  },
  "ai-automation-guide": {
    guideId: "ai-automation-guide",
    videoTitle: "How to Build an AI Automation Workflow",
    introLine: "Formulate event-triggered structures that handle manual operations.",
    durationTarget: "3:45",
    ctaEnding: "Start AI Automation",
    scenes: [
      {
        title: "Choose a Trigger",
        text: "Start with an event.",
        voiceover: "An automation begins when something happens, like a form, email, search, or task.",
        visualDirection: "A glowing lightning bolt icon triggering a cascade of digital nodes down a futuristic track."
      },
      {
        title: "Add an Agent",
        text: "Let the agent prepare the work.",
        voiceover: "NexoraX Agents can draft, organise, summarise, or prepare outputs.",
        visualDirection: "The NexoraX core avatar spinning gently as streams of text flow through its field."
      },
      {
        title: "Add Approval",
        text: "Keep important actions reviewed.",
        voiceover: "Sensitive actions should wait for human approval.",
        visualDirection: "A secure digital gate that glows red until an approved handshake unlocks it."
      },
      {
        title: "Create Output",
        text: "Task, email draft, report, campaign.",
        voiceover: "Choose what the workflow should produce.",
        visualDirection: "Multiple glowing icons representing emails, documents, and tasks materializing simultaneously."
      },
      {
        title: "Test and Activate",
        text: "Launch after review.",
        voiceover: "Test the workflow before using it with real clients.",
        visualDirection: "A live pulse monitor signal showing healthy regular heartbeats of an active continuous workspace workflow."
      }
    ]
  },
  "voice-matrix-guide": {
    guideId: "voice-matrix-guide",
    videoTitle: "How to Use NexoraX Voice Matrix",
    introLine: "Establish interactive voice channels mapped to physical hotlines.",
    durationTarget: "2:50",
    ctaEnding: "Explore Voice Matrix",
    scenes: [
      {
        title: "Voice Assistant Setup",
        text: "Create a voice-based customer guide.",
        voiceover: "Voice Matrix helps prepare voice assistant flows for website and support use.",
        visualDirection: "Elegant 3D sound waves of light shifting and pulsing over a pitch-black digital landscape."
      },
      {
        title: "Add Introduction",
        text: "Welcome visitors clearly.",
        voiceover: "Start with a friendly introduction that explains how the assistant can help.",
        visualDirection: "Audio levels expanding sideways as a text box reveals the welcome message."
      },
      {
        title: "Add Service Knowledge",
        text: "Teach the assistant your services.",
        voiceover: "Add website, pricing, support, and booking information.",
        visualDirection: "A glowing neural map absorbing knowledge modules and connecting points."
      },
      {
        title: "Test Questions",
        text: "Check answers before launch.",
        voiceover: "Run sample customer questions to improve responses.",
        visualDirection: "Simulated audio testing waveforms rising and falling with successful response feedback tags."
      },
      {
        title: "Connect to Support",
        text: "Move enquiries into workflow.",
        voiceover: "Important enquiries can connect to support, booking, or WorkSuite.",
        visualDirection: "A stream of secure customer requests migrating into a central workflow column smoothly."
      }
    ]
  },
  "billing-guide": {
    guideId: "billing-guide",
    videoTitle: "Understanding Search Credits, Plans, and Invoices",
    introLine: "Verify credit allowances and structural invoice lists.",
    durationTarget: "1:55",
    ctaEnding: "View Payment Tiers",
    scenes: [
      {
        title: "Search Credits",
        text: "Credits power successful searches.",
        voiceover: "NexoraX Search credits track successful non-cached searches.",
        visualDirection: "A glowing circular fuel-style indicator showing remaining credits in premium cyan digital ink."
      },
      {
        title: "Plans and Add-ons",
        text: "Choose what your business needs.",
        voiceover: "Plans can include hosting, search, email, automation, and support.",
        visualDirection: "Three elegant glass cards displaying tiered packages from Bronze to Singularity lifetime tiers."
      },
      {
        title: "Invoice Register",
        text: "Keep records organised.",
        voiceover: "Billing records help track invoices, receipts, usage, and payments.",
        visualDirection: "A digital ledger sliding onto the screen showing neat rows of structured invoice items with matching receipts."
      },
      {
        title: "Payment Reconciliation",
        text: "Match payments to records.",
        voiceover: "Payment reconciliation helps confirm what has been paid and what needs review.",
        visualDirection: "A split-screen checking match where balance figures on both sides light up in green matching success tags."
      },
      {
        title: "Accountant Export",
        text: "Prepare records for review.",
        voiceover: "Export summaries can support accounting review when needed.",
        visualDirection: "An elegant export button triggering a clean file download file animation glowing with gold accents."
      }
    ]
  },
  "privacy-consent-guide": {
    guideId: "privacy-consent-guide",
    videoTitle: "How Privacy and Cookie Settings Work",
    introLine: "Manage direct cookie options and data inquiry submissions.",
    durationTarget: "1:30",
    ctaEnding: "Open Cookie Settings",
    scenes: [
      {
        title: "Cookie Choices",
        text: "Accept, reject, or manage.",
        voiceover: "Visitors can choose which optional cookies and features they allow.",
        visualDirection: "A beautiful, transparent cookie consent card sliding up with direct accept/reject/manage buttons."
      },
      {
        title: "Preference Center",
        text: "Control optional settings.",
        voiceover: "The preference center lets users manage analytics, marketing, and personalisation choices.",
        visualDirection: "A configuration list showing customized switches turning on/off with elegant fluid animations."
      },
      {
        title: "Privacy Rights",
        text: "Request access, correction, or deletion.",
        voiceover: "Users can submit privacy rights requests through the public request form.",
        visualDirection: "A user silhouette surrounded by glowing legal shield parameters highlighting access, correct, delete controls."
      },
      {
        title: "Consent Records",
        text: "Choices are recorded safely.",
        voiceover: "Consent choices should be stored server-side for review and withdrawal.",
        visualDirection: "An encrypted server database vault registering secure audit tokens of consents successfully."
      },
      {
        title: "Legal Footer",
        text: "Policies stay easy to find.",
        voiceover: "Privacy, cookie, terms, accessibility, and contact links remain available in the footer.",
        visualDirection: "An elegant web page footer highlighting deep blue compliance link paths and standards seals."
      }
    ]
  }
};
